import { defineStore } from 'pinia';
import { MpcStatisticsByDayLineDepartureRow, MpcStatisticsLine } from './mpcStatisticsModels';
import { Link } from '@/shared/models';
import { MpcStatisticsRels, MpcStatisticsRootRel } from './rels';
import { useCommonStore } from '@/shared/commonStore';
import { useAxios } from '@/shared/axios';
import { FormatDateISO } from '@/shared/DateFormatHelpers';

function currentYearMonth(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export const useMpcStatisticsStore = defineStore('mpcStatistics', {
    state: () => ({
        mpcStatistics: new Array<MpcStatisticsLine>(),
        currentDate: FormatDateISO(new Date()),
        links: new Array<Link>(),
        rootLinks: new Array<Link>(),
        byDayLineDeparture: null as MpcStatisticsByDayLineDepartureRow[] | null,
        monthlyPeriod: { from: currentYearMonth(), to: currentYearMonth() }
    }),
    getters: {
        getRootLink(state) {
            return (rel: string): Link | undefined => state.rootLinks.find(x => x.rel === rel);
        }
    },
    actions: {
        async loadRootLinks() {
            const commonStore = useCommonStore();
            const axios = useAxios();
            const link = commonStore.getLink(MpcStatisticsRootRel);

            const response = await axios.requestByLink(link);
            this.rootLinks = response.data.links;
        },
        async loadMpcStatistics() {
            if (this.rootLinks.length === 0) await this.loadRootLinks();
            const axios = useAxios();
            const link = this.rootLinks.find(x => x.rel === MpcStatisticsRels.getMpcStatistics);
            if (!link) throw new Error('Daily statistics link not found');

            const params = new URLSearchParams();
            params.append('calendarDay', this.currentDate);

            const response = await axios.requestByLink(link, { params });

            this.mpcStatistics = response.data.data;
            this.links = response.data.links;
        },
        async loadByDayLineDeparture(from: string, to: string) {
            if (this.rootLinks.length === 0) await this.loadRootLinks();
            const axios = useAxios();
            const link = this.rootLinks.find(x => x.rel === MpcStatisticsRels.getMpcStatisticsByDayLineDeparture);
            if (!link) throw new Error('By-day-line-departure link not found');

            this.monthlyPeriod = { from, to };
            const params = new URLSearchParams();
            params.append('from', from + '-01');
            params.append('to', toLastDay(to));

            const response = await axios.requestByLink(link, { params });
            this.byDayLineDeparture = response.data.data;
        },
        async getExportUrl(from: string, to: string): Promise<string> {
            if (this.rootLinks.length === 0) await this.loadRootLinks();
            const link = this.rootLinks.find(x => x.rel === MpcStatisticsRels.exportMpcStatistics);
            if (!link) throw new Error('Export link not found');
            const sep = link.href.includes('?') ? '&' : '?';
            return `${link.href}${sep}from=${encodeURIComponent(from + '-01')}&to=${encodeURIComponent(toLastDay(to))}`;
        }
    }
});

function toLastDay(yearMonth: string): string {
    const [y, m] = yearMonth.split('-').map(Number);
    const d = new Date(y, m, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
